import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { ArrowLeft, ChartLineUp, VideoCamera, Clock, Waveform, Users, Broadcast, HardDrives, Broom, DownloadSimple } from "@phosphor-icons/react";

const fmtDate = (s) => { try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return "—"; } };

async function downloadCsv(url, filename) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = filename;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error(apiError(e)); }
}

export default function MeetAnalytics() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["meet-analytics"],
    queryFn: async () => (await api.get("/meet/analytics")).data,
    retry: false,
  });

  const cleanup = async (payload, label) => {
    if (!window.confirm(`${label}\n\nThis permanently deletes the recording files. Continue?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/meet/recordings/cleanup", payload);
      toast.success(r.deleted ? `Removed ${r.deleted} recording(s), freed ${r.freed_mb} MB` : "Nothing old enough to remove");
      qc.invalidateQueries({ queryKey: ["meet-analytics"] });
      qc.invalidateQueries({ queryKey: ["meet-usage"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  if (isLoading) return <div className="p-6 text-muted-foreground">Loading analytics…</div>;
  if (isError) {
    return (
      <div className="space-y-4" data-testid="analytics-no-access">
        <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground"><ArrowLeft size={15} /> Back</button>
        <div className="rounded-2xl border border-border bg-card p-6">
          <h1 className="font-display text-2xl font-semibold">Meeting Analytics</h1>
          <p className="mt-2 text-muted-foreground">{error?.response?.data?.detail || "Only an organization admin can view analytics."}</p>
        </div>
      </div>
    );
  }

  const t = data.totals;
  const caps = data.caps || {};
  const storagePct = caps.recording_storage_mb ? Math.min(100, Math.round((t.storage_mb / caps.recording_storage_mb) * 100)) : 0;
  const nearCap = storagePct >= 80;
  const cards = [
    { label: "Meetings", value: t.meetings, icon: VideoCamera },
    { label: "Total minutes", value: t.minutes, icon: Clock },
    { label: "Recordings", value: t.recordings, icon: Waveform },
    { label: "Attendees", value: t.attendees, icon: Users },
    { label: "Broadcasts", value: t.broadcasts, icon: Broadcast },
    { label: "Broadcast reach", value: t.broadcast_reach, sub: `peak ${t.broadcast_peak}`, icon: ChartLineUp },
  ];
  const capBars = [
    { label: "Recording storage", used: t.storage_mb, cap: caps.recording_storage_mb, unit: "MB", icon: HardDrives },
    { label: "Max participants / meeting", used: null, cap: caps.max_participants, unit: "", icon: Users },
    { label: "Simultaneous broadcasts", used: null, cap: caps.max_broadcasts, unit: "", icon: Broadcast },
  ];

  return (
    <div className="space-y-6" data-testid="meet-analytics">
      <button onClick={() => navigate("/app/meet")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft size={15} /> Back to meetings</button>
      <div>
        <h1 className="flex items-center gap-2 font-display text-3xl font-semibold tracking-tight"><ChartLineUp size={28} weight="duotone" /> Meeting Analytics</h1>
        <p className="mt-2 text-muted-foreground">Usage across your organization — minutes, recordings, attendance and broadcast reach. Plan: <span className="font-semibold capitalize text-foreground">{caps.tier}</span>.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {cards.map((c) => (
          <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="rounded-2xl border border-border bg-card p-4">
            <c.icon size={20} weight="duotone" className="text-primary" />
            <p className="mt-2 font-display text-2xl font-semibold tabular-nums">{c.value}</p>
            <p className="text-xs text-muted-foreground">{c.label}{c.sub ? ` · ${c.sub}` : ""}</p>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Plan entitlements</h2>
          {nearCap && <span className="rounded-full bg-destructive/15 px-2.5 py-1 text-xs font-semibold text-destructive" data-testid="storage-warning">Storage {storagePct}% full</span>}
        </div>
        <div className="space-y-3">
          {capBars.map((b) => {
            const pct = b.used != null && b.cap ? Math.min(100, Math.round((b.used / b.cap) * 100)) : null;
            return (
              <div key={b.label} data-testid={`cap-${b.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5"><b.icon size={15} weight="duotone" className="text-muted-foreground" /> {b.label}</span>
                  <span className="tabular-nums text-muted-foreground">{b.used != null ? `${b.used}${b.unit} / ` : ""}{b.cap}{b.unit}</span>
                </div>
                {pct != null && (
                  <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-secondary">
                    <div className={`h-full rounded-full ${pct >= 90 ? "bg-destructive" : pct >= 80 ? "bg-amber-500" : "bg-primary"}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
          <button data-testid="cleanup-under-cap" disabled={busy} onClick={() => cleanup({ target_under_cap: true }, "Free up space — remove the oldest recordings until storage is back under 80%.")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-60 ${nearCap ? "bg-destructive text-destructive-foreground animate-pulse" : "border border-border hover:border-primary"}`}>
            <Broom size={15} weight="duotone" /> Free up space
          </button>
          <button data-testid="cleanup-90" disabled={busy} onClick={() => cleanup({ older_than_days: 90 }, "Clear every meeting recording older than 90 days.")}
            className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary disabled:opacity-60">
            <Broom size={15} weight="duotone" /> Clear older than 90 days
          </button>
          <p className="w-full text-xs text-muted-foreground">Removes <b>your</b> oldest meeting recordings. Add-ons and overages keep your archives — this only clears space you choose to free.</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h2 className="mb-3 text-lg font-semibold">Recent meetings</h2>
        {data.recent.length === 0 ? (
          <p className="text-sm text-muted-foreground">No meetings yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-recent">
              <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className="py-2 pr-3">Meeting</th><th className="py-2 pr-3">Mode</th><th className="py-2 pr-3">Minutes</th>
                <th className="py-2 pr-3">Attendees</th><th className="py-2 pr-3">Recordings</th><th className="py-2 pr-3">Date</th><th className="py-2 pr-3">Export</th>
              </tr></thead>
              <tbody>
                {data.recent.map((m) => (
                  <tr key={m.code} className="border-b border-border/50">
                    <td className="py-2 pr-3 font-medium">{m.title}</td>
                    <td className="py-2 pr-3 capitalize text-muted-foreground">{m.mode}</td>
                    <td className="py-2 pr-3 tabular-nums">{m.minutes}</td>
                    <td className="py-2 pr-3 tabular-nums">{m.attendees}</td>
                    <td className="py-2 pr-3 tabular-nums">{m.recordings}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{fmtDate(m.created_at)}</td>
                    <td className="py-2 pr-3">
                      <div className="flex items-center gap-1.5">
                        <button data-testid={`export-attendance-${m.code}`} disabled={!m.attendees} title="Attendance CSV" onClick={() => downloadCsv(`/meetings/${m.code}/attendance.csv`, `attendance-${m.code}.csv`)}
                          className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs hover:border-primary disabled:opacity-40"><DownloadSimple size={13} /> Roll</button>
                        {m.has_quiz && <button data-testid={`export-quiz-${m.code}`} title="Quiz scores CSV" onClick={() => downloadCsv(`/meetings/${m.code}/quiz.csv`, `quiz-scores-${m.code}.csv`)}
                          className="flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs hover:border-primary"><DownloadSimple size={13} /> Quiz</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
